import type { Map as MapLibreMap, StyleSpecification } from "maplibre-gl";
import style from "./basemap-style.json";

/** A fresh copy keeps MapLibre's runtime changes out of other map instances. */
export function darkBasemapStyle(): StyleSpecification {
  return structuredClone(style) as StyleSpecification;
}

type Bounds = { west: number; south: number; east: number; north: number };
type SnapshotOptions = {
  width: number;
  height: number;
  bounds?: Bounds;
  center?: [number, number];
  zoom?: number;
  signal: AbortSignal;
};

/** Render vector tiles once; retain only a 2D canvas, never a hidden live WebGL map. */
export async function renderBasemapSnapshot(options: SnapshotOptions): Promise<{ canvas: HTMLCanvasElement; bounds: Bounds } | null> {
  if (options.signal.aborted) return null;
  const { default: maplibregl } = await import("maplibre-gl");
  if (options.signal.aborted) return null;
  const container = document.createElement("div");
  container.setAttribute("aria-hidden", "true");
  Object.assign(container.style, {
    position: "fixed", left: "-10000px", top: "0", pointerEvents: "none",
    width: `${Math.max(1, Math.round(options.width))}px`,
    height: `${Math.max(1, Math.round(options.height))}px`,
  });
  document.body.append(container);
  let map: MapLibreMap | undefined;
  try {
    map = new maplibregl.Map({
      container,
      style: darkBasemapStyle(),
      center: options.center ?? [0, 0],
      zoom: options.zoom ?? 9,
      interactive: false,
      attributionControl: false, // The page's linked information panel credits all maps.
      canvasContextAttributes: { preserveDrawingBuffer: true },
      fadeDuration: 0,
    });
    if (options.bounds) {
      const b = options.bounds;
      map.fitBounds([[b.west, b.south], [b.east, b.north]], { padding: 0, duration: 0, maxZoom: 14 });
    }
    const ready = await new Promise<boolean>((resolve) => {
      const finish = (ok: boolean) => {
        clearTimeout(timeout);
        options.signal.removeEventListener("abort", abort);
        map!.off("idle", idle);
        resolve(ok);
      };
      const abort = () => finish(false);
      const idle = () => finish(true);
      const timeout = window.setTimeout(() => finish(false), 15000);
      options.signal.addEventListener("abort", abort, { once: true });
      map!.once("idle", idle);
      if (options.signal.aborted) abort();
    });
    if (!ready || options.signal.aborted) return null;
    const canvas = document.createElement("canvas");
    const source = map.getCanvas();
    canvas.width = source.width;
    canvas.height = source.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0);
    const b = map.getBounds();
    return { canvas, bounds: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() } };
  } catch {
    return null;
  } finally {
    map?.remove();
    container.remove();
  }
}
